import { Box, Grid } from '@mui/material';
import { camelCase, snakeCase } from 'lodash';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import SmallDonutChart from 'spw-components/charts/small-donut-chart';
import {
  AMENITY_SPACES_CATEGORY,
  INDIVIDUAL_WORKSPACES_CATEGORY,
  MEETING_SPACES_CATEGORY,
  SHARED_SPACES_CATEGORY,
  SUPPORT_SPACES_CATEGORY,
} from 'spw-constants';
import {
  BenchmarkReportCardType,
  BenchmarkReportCategoryUSFAllocation,
  BenchmarkReportIconType,
} from 'spw-redux/modules/benchmark-reports/types';
import { Row } from 'spw-shared/globalComponents';
import { getBalancedRoundedPercentages } from 'spw-shared/utils';
import { colors } from 'spw-theme';
import styled from 'styled-components';
import {
  selectCurrentBenchmarkReportCategoriesMap,
  selectCurrentBenchmarkReportIconsMap,
} from 'spw-redux/modules/benchmark-reports/selectors';
import useUnitConversion from 'spw-components/unit-conversion/hooks';

const Container = styled.div``;

const Lengend = styled.div`
  .percentage {
    color: ${colors.black};
    font-size: 32px;
    font-style: normal;
    font-weight: 500;
    line-height: 34px;
  }

  img {
    width: 40px;
    height: 40px;
    margin-left: 8px;
  }
`;

const Category = styled(Row)`
  .dot {
    width: 16px;
    height: 16px;
    border-radius: 8px;
    margin-right: 8px;
  }

  .name {
    color: ${colors.fontColor};
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    text-transform: uppercase;
  }
`;

const LeftUpperBracket = styled.div`
  margin-top: 6px;
  height: 12px;
  flex: 1;
  border-top: 4px solid ${colors.containerBorder};
  border-left: 4px solid ${colors.containerBorder};
`;
const RightUpperBracket = styled(LeftUpperBracket)`
  border-left: none;
  border-right: 4px solid ${colors.containerBorder};
`;

const LowerBracket = styled(Box)`
  height: 12px;
  border-bottom: 4px solid ${colors.containerBorder};
  border-left: 4px solid ${colors.containerBorder};
  border-right: 4px solid ${colors.containerBorder};
`;

const FloatingPercentage = styled(Box)`
  position: absolute;
  top: 50%;
  left: 50%;
  margin-left: -30px;
  margin-top: -12px;
  text-align: center;
  font-size: 18px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  width: 60px;
`;

type CategoriesAllocation = {
  individualWorkspaces: number;
  meetingSpaces: number;
  amenitySpaces: number;
  supportSpaces: number;
};

const SharedCategoryDonutGraph = ({
  mainCategory,
  allocation,
}: {
  mainCategory: keyof CategoriesAllocation;
  allocation: CategoriesAllocation;
}) => {
  const currentReportCategories = useSelector(
    selectCurrentBenchmarkReportCategoriesMap
  );

  const sharedColors: string[] = [];
  const sharedCategoriesData: (string | number)[][] = [];
  const sharedCategories: string[] = [
    MEETING_SPACES_CATEGORY,
    AMENITY_SPACES_CATEGORY,
    SUPPORT_SPACES_CATEGORY,
  ];
  sharedCategories.forEach((category: string) => {
    let color;

    if (category === mainCategory) {
      color = currentReportCategories[category].color;
    } else {
      color = currentReportCategories[SHARED_SPACES_CATEGORY].color;
    }

    sharedColors.push(color);
    sharedCategoriesData.push([
      category,
      allocation[category as keyof CategoriesAllocation],
    ]);
  });

  return (
    <Box position="relative" height={96} width={96}>
      <SmallDonutChart
        data={[
          ['Category', 'USF Allocation'],
          ['IW Spaces', allocation.individualWorkspaces],
          ...sharedCategoriesData,
        ]}
        colors={['#ffffff', ...sharedColors]}
        options={{ width: 96, height: 96 }}
      />
      <FloatingPercentage data-testid={`${mainCategory}_usf_allocation`}>
        {(
          allocation[mainCategory as keyof CategoriesAllocation] || 0
        ).toLocaleString('en', {
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        })}
        %
      </FloatingPercentage>
    </Box>
  );
};
const SharedCategoryName = ({
  mainCategory,
  icon,
  categoryName,
}: {
  mainCategory: keyof CategoriesAllocation;
  icon: string;
  categoryName: string;
}) => {
  const currentReportCategories = useSelector(
    selectCurrentBenchmarkReportCategoriesMap
  );
  return (
    <Box
      ml={2}
      height={96}
      display="flex"
      flexDirection="column"
      justifyContent="center"
      data-cy={`${snakeCase(categoryName.toLowerCase())}_category_name`}
    >
      <Box ml={3}>
        <img src={icon} alt={mainCategory} height={28} />
      </Box>
      <Category alignItems="center" mt={1.5}>
        <Box
          className="dot"
          bgcolor={currentReportCategories[mainCategory].color}
        />
        <div className="name">{categoryName}</div>
      </Category>
    </Box>
  );
};

const cardType = BenchmarkReportCardType.usfAllocationBySpaceCategory;

const USFAllocationBySpaceCategory = ({
  allocationBySpaceCategory,
}: {
  allocationBySpaceCategory: BenchmarkReportCategoryUSFAllocation[];
}) => {
  const { getUnit } = useUnitConversion();
  const currentReportCategories = useSelector(
    selectCurrentBenchmarkReportCategoriesMap
  );
  const currentReportIconsMap = useSelector(
    selectCurrentBenchmarkReportIconsMap
  );
  const allocation: CategoriesAllocation = useMemo(() => {
    const defaultAllocation = {} as CategoriesAllocation;
    return (
      allocationBySpaceCategory?.reduce((map, allocationByCategory) => {
        return {
          ...map,
          [camelCase(allocationByCategory.category)]:
            allocationByCategory.usfAllocationPercent * 100,
        };
      }, defaultAllocation) || defaultAllocation
    );
  }, [allocationBySpaceCategory]);

  const sharedSpacesAllocation =
    (allocation?.meetingSpaces || 0) +
    (allocation?.amenitySpaces || 0) +
    (allocation?.supportSpaces || 0);

  const getAllocationChartData = (category: keyof CategoriesAllocation) => {
    return [category, allocation?.[category] || 0];
  };

  const balancedRoundedPercentages = getBalancedRoundedPercentages([
    {
      id: 'individualWorkspaces',
      percentage: allocation.individualWorkspaces || 0,
    },
    { id: 'sharedSpaces', percentage: sharedSpacesAllocation },
  ]);

  const getIcon = (iconType: BenchmarkReportIconType) =>
    `/images/space-icons/${currentReportIconsMap[cardType][iconType]}.svg`;

  return (
    <Container>
      <div className="title">
        {getUnit('area')} ALLOCATION BY SPACE CATEGORY
      </div>
      <Grid container>
        <Grid item xs={5}>
          <Box
            mb={6}
            height={164}
            width={164}
            data-cy="usf_allocation_by_space_category_donut_chart"
          >
            <SmallDonutChart
              data={[
                ['Category', 'USF Allocation'],
                getAllocationChartData('individualWorkspaces'),
                getAllocationChartData('meetingSpaces'),
                getAllocationChartData('amenitySpaces'),
                getAllocationChartData('supportSpaces'),
              ]}
              colors={[
                currentReportCategories[INDIVIDUAL_WORKSPACES_CATEGORY].color,
                currentReportCategories[SHARED_SPACES_CATEGORY].color,
                currentReportCategories[SHARED_SPACES_CATEGORY].color,
                currentReportCategories[SHARED_SPACES_CATEGORY].color,
              ]}
              options={{ width: 164, height: 164 }}
            />
          </Box>

          <Lengend>
            <Row alignItems="center" mb={1.5}>
              <div className="percentage" data-testid="iw_usf_allocation">
                {balancedRoundedPercentages[0].percentage}%
              </div>
              <img
                src={getIcon(BenchmarkReportIconType.individualWorkspaces)}
                alt=""
              />
            </Row>
            <Category alignItems="center">
              <Box
                className="dot"
                bgcolor={
                  currentReportCategories[INDIVIDUAL_WORKSPACES_CATEGORY].color
                }
              />
              <div className="name">Individual Workspaces</div>
            </Category>
          </Lengend>
          <Box mt={4}>
            <Lengend>
              <Row alignItems="center" mb={1.5}>
                <div
                  className="percentage"
                  data-testid="sharedSpaces_usf_allocation"
                >
                  {Number.isNaN(sharedSpacesAllocation)
                    ? ''
                    : balancedRoundedPercentages[1].percentage}
                  %
                </div>
                <img
                  src={getIcon(BenchmarkReportIconType.meetingSpaces)}
                  alt=""
                />
                <img
                  src={getIcon(BenchmarkReportIconType.amenitySpaces)}
                  alt=""
                />
                <img
                  src={getIcon(BenchmarkReportIconType.supportSpaces)}
                  alt=""
                />
              </Row>
              <Category alignItems="center">
                <Box
                  className="dot"
                  bgcolor={
                    currentReportCategories[SHARED_SPACES_CATEGORY].color
                  }
                />
                <div className="name">Shared Spaces</div>
              </Category>
            </Lengend>
          </Box>
        </Grid>
        <Grid item xs={7}>
          <Row mb={1.5}>
            <LeftUpperBracket />
            <Category
              alignItems="center"
              width="204px"
              justifyContent="center"
              mb={1}
            >
              <Box
                className="dot"
                bgcolor={currentReportCategories[SHARED_SPACES_CATEGORY].color}
              />
              <div
                className="name"
                style={{ fontWeight: '500', color: colors.black }}
              >
                Shared Spaces
              </div>
            </Category>
            <RightUpperBracket />
          </Row>
          <Box display="flex" justifyContent="center">
            <Box>
              {[
                MEETING_SPACES_CATEGORY,
                AMENITY_SPACES_CATEGORY,
                SUPPORT_SPACES_CATEGORY,
              ].map((category: keyof CategoriesAllocation, index: number) => {
                return (
                  <Box
                    key={category}
                    mt={index > 0 ? 2 : 0}
                    data-cy={`${category}_usf_allocation_donut_chart`}
                  >
                    <SharedCategoryDonutGraph
                      mainCategory={category}
                      allocation={allocation}
                    />
                  </Box>
                );
              })}
            </Box>
            <Box>
              {[
                MEETING_SPACES_CATEGORY,
                AMENITY_SPACES_CATEGORY,
                SUPPORT_SPACES_CATEGORY,
              ].map((category: string, index: number) => (
                <Box key={category} mt={index > 0 ? 2 : 0}>
                  <SharedCategoryName
                    mainCategory={category as keyof CategoriesAllocation}
                    icon={getIcon(
                      BenchmarkReportIconType[
                        category as keyof typeof BenchmarkReportIconType
                      ]
                    )}
                    categoryName={currentReportCategories[category].name}
                  />
                </Box>
              ))}
            </Box>
          </Box>
          <LowerBracket mt={1.5} />
        </Grid>
      </Grid>
    </Container>
  );
};

export default USFAllocationBySpaceCategory;
